import { FormControl, FormHelperText, FormLabel, InputLabel, MenuItem, Select } from '@mui/material';
import { DotsLoader } from 'Components/DotsLoader';
import { ButtonForm, CustomTextField } from 'Components/Form/styles';
import { Column, Row } from 'Components/Grid';
import { MuiColorInput } from 'mui-color-input';
import { Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const Configuration = ({ id, register, control, handleSubmit, errors, getValues, handleComplete, loading }) => {
  const { t } = useTranslation(['cookies']);
  const navigate = useNavigate();
  const onSubmit = () => {
    handleComplete();
  };

  const positions = [
    {
      value: 'left',
      description: t('cookies:form.config.positionLeft'),
    },
    {
      value: 'right',
      description: t('cookies:form.config.positionRight'),
    },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Column>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontWeight: 'bold', marginBottom: '24px' }}>
              {t('cookies:form.config.sectionTitle')}
            </FormLabel>
          </FormControl>
        </Column>
      </Row>
      <Row justifyContent="center" className="mb-3">
        <Column xs={12} md={6} className="d-flex">
          <div data-tour="cookie-url" style={{ width: '100%' }}>
          <CustomTextField
            {...register('url', { required: t('cookies:form.config.urlRequired') })}
            label={t('cookies:form.config.urlLabel')}
            autoFocus
            error={errors?.url ? true : false}
            sx={{ minHeight: '48px', maxHeight: '48px', margin: '0 auto', width: '100%' }}
            helperText={errors?.url ? errors.url.message : null}
            InputLabelProps={{ shrink: true }}
            disabled={!!id}
          />
          </div>
        </Column>
        <Column xs={12} md={6} className="d-flex">
          <div data-tour="cookie-position" style={{ width: '100%' }}>
          <Controller
            control={control}
            rules={{
              validate: () => {
                return getValues('positionController') !== '';
              },
              required: t('cookies:form.config.positionRequired'),
            }}
            name="positionController"
            render={({ field: { onChange, value } }) => (
              <FormControl fullWidth sx={{ margin: '0' }} error={errors?.positionCard ? true : false}>
                <InputLabel id="position-controller-label" shrink={true}>
                  {t('cookies:form.config.positionLabel')}
                </InputLabel>
                <Select
                  labelId="position-controller-label"
                  id="positionController"
                  value={value ?? ''}
                  label={t('cookies:form.config.positionLabel')}
                  onChange={onChange}
                  notched
                  sx={{ '> #positionController': { padding: '11.5px 14px' } }}
                >
                  <MenuItem value={''} key={-1}>
                    {t('cookies:form.config.positionPlaceholder')}
                  </MenuItem>
                  {positions?.map(item => (
                    <MenuItem value={`${item.value}`} key={`position-${item.value}`}>
                      {item.description}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors?.positionCard ? errors.positionCard.message : ''}</FormHelperText>
              </FormControl>
            )}
          />
          </div>
        </Column>
      </Row>
      <div data-tour="cookie-colors">
      <Row className="mb-3">
        <Column xs={12} md={4} className="d-flex">
          <Controller
            control={control}
            rules={{
              validate: () => {
                return getValues('primaryColor') !== '';
              },
              required: t('cookies:form.config.primaryColorRequired'),
            }}
            name="primaryColor"
            render={({ field: { onChange, value } }) => (
              <FormControl fullWidth error={errors?.primaryColor ? true : false}>
                <InputLabel id="primary-Color-label" shrink={true}>
                  {t('cookies:form.config.primaryColorLabel')}
                </InputLabel>
                <MuiColorInput label={t('cookies:form.config.primaryColorLabel')} format="hex" value={value} onChange={onChange} isAlphaHidden />
                <FormHelperText>{errors?.primaryColor ? errors.primaryColor.message : ''}</FormHelperText>
              </FormControl>
            )}
          />
        </Column>
        <Column xs={12} md={4} className="d-flex">
          <Controller
            control={control}
            rules={{
              validate: () => {
                return getValues('backgroundColor') !== '';
              },
              required: t('cookies:form.config.bgColorRequired'),
            }}
            name="backgroundColor"
            render={({ field: { onChange, value } }) => (
              <FormControl fullWidth error={errors?.backgroundColor ? true : false}>
                <InputLabel id="background-color-label" shrink={true}>
                  {t('cookies:form.config.bgColorLabel')}
                </InputLabel>
                <MuiColorInput label={t('cookies:form.config.bgColorLabel')} format="hex" value={value} onChange={onChange} isAlphaHidden />
                <FormHelperText>{errors?.backgroundColor ? errors.backgroundColor.message : ''}</FormHelperText>
              </FormControl>
            )}
          />
        </Column>
        <Column xs={12} md={4} className="d-flex">
          <Controller
            control={control}
            rules={{
              validate: () => {
                return getValues('textColor') !== '';
              },
              required: t('cookies:form.config.textColorRequired'),
            }}
            name="textColor"
            render={({ field: { onChange, value } }) => (
              <FormControl fullWidth error={errors?.textColor ? true : false}>
                <InputLabel id="text-color-label" shrink={true}>
                  {t('cookies:form.config.textColorLabel')}
                </InputLabel>
                <MuiColorInput label={t('cookies:form.config.textColorLabel')} format="hex" value={value} onChange={onChange} isAlphaHidden />
                <FormHelperText>{errors?.textColor ? errors.textColor.message : ''}</FormHelperText>
              </FormControl>
            )}
          />
        </Column>
      </Row>
      </div>
      <div data-tour="form-actions">
      <Row justifyContent="end" alignItems="center">
        <Column xs={4} md={3} className="d-flex" style={{ gap: '8px', justifyContent: 'flex-end' }}>
          <ButtonForm
            secondary
            id="cancel"
            label={t('cookies:form.config.cancelButton')}
            onClick={() => navigate('/cookies')}
            disabled={loading}
          ></ButtonForm>
          <ButtonForm label={loading ? <DotsLoader /> : t('cookies:form.config.saveButton')} type="submit" disabled={loading} />
        </Column>
      </Row>
      </div>
    </form>
  );
};
